import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { satisfaction as api } from '../../services/api';
import permissionsApi from '../../services/permissions';
import { etiquetteFormulaire, estPassage, VehiculeEnvoi } from '../SatisfactionCommun';
import {
  ActionsContact, Bloc, CarteLigne, Chiffres, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Puces, Recherche,
  depuis, messageErreur, quand, useFicheOuverte, useToast,
} from './MobileUI';
import './SatisfactionMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Satisfaction client — version téléphone (2026-10-24).
//
// Le cas d'usage : la personne qui rappelle les détracteurs (directeur de
// site, chef d'atelier, chef des ventes) le fait depuis le terrain. Elle
// arrive sur « À traiter » ; les détracteurs non rappelés remontent en tête,
// en rouge. L'enchaînement visé : voir le détracteur, l'appeler (bouton
// Appeler, absent de l'ordinateur), revenir — la feuille « Traiter » s'ouvre
// alors sur le canal Appel —, écrire deux phrases, marquer traité.
//
// Mêmes routes et mêmes règles que Satisfaction.js / SatisfactionReponseModal.js :
//   - GET /satisfaction/envois (statut = repondu, file = traitement, recherche
//     côté serveur avec un délai de frappe) : seuls les clients qui ont
//     répondu ; les compteurs des files viennent du serveur ;
//   - chiffres du haut : GET /satisfaction/tableau-bord (score NPS et note
//     moyenne sur 30 jours, détracteurs à rappeler sans limite de date) ;
//   - fiche : GET /satisfaction/envois/:id ; « Traiter » : POST …/traitements
//     (note obligatoire pour Appel / Sur place ; message, SMS ≤ 160 caractères
//     désaccentués, objet pour l'e-mail), rédaction IA : POST …/brouillon.
//     Droit « modifier » ; sans lui, la fiche est en lecture seule ;
//   - le sélecteur de concession global (menu utilisateur) filtre tout : la
//     concession active est rappelée en tête.
// Reste sur ordinateur : Envois, Observation, Tableau de bord, Formulaires,
// Charte, suppression, « Annuler le marquage ».
// ═══════════════════════════════════════════════════════════════════════════

const FILES = [
  { cle: 'a_traiter', libelle: 'À traiter' },
  { cle: 'detracteurs_a_traiter', libelle: 'Détracteurs', alerte: true },
  { cle: 'traite', libelle: 'Traitées' },
  { cle: 'toutes', libelle: 'Toutes' },
];
const CANAUX = [
  { cle: 'telephone', icone: '📞', libelle: 'Appel', emet: false },
  { cle: 'sms', icone: '📱', libelle: 'SMS', emet: true },
  { cle: 'mail', icone: '✉️', libelle: 'E-mail', emet: true },
  { cle: 'sur_place', icone: '🏢', libelle: 'Sur place', emet: false },
];
const TRAITEMENT_LIBELLE = { telephone: 'Tél.', sms: 'SMS', mail: 'E-mail', sur_place: 'Sur place', note: 'Note' };
const CANAL_LABEL = { telephone: 'téléphone', sms: 'SMS', mail: 'e-mail', sur_place: 'sur place', note: 'note' };
const CANAL_ENVOI = { email: 'e-mail', sms: 'SMS', 'sms+email': 'SMS + e-mail' };
const FINALITE = { prospection_tel: 'Téléphone', prospection_sms: 'SMS', prospection_email: 'E-mail' };
const SMS_MAX = 160;

// Longueur comptée comme Mobbiz et le serveur : texte désaccentué.
const longueurSms = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').length;

const nomClient = (e) => [e.client_prenom, e.client_nom].filter(Boolean).join(' ') || 'Client';
const aNote = (e) => e.nps !== null && e.nps !== undefined;
const estDetracteur = (e) => aNote(e) && Number(e.nps) <= 6;
const tonNote = (n) => (n === null || n === undefined ? 'vide' : Number(n) >= 9 ? 'pro' : Number(n) >= 7 ? 'neutre' : 'det');

// « 16/10 », l'année seulement si ce n'est pas l'année en cours (comme l'ordinateur).
function dateCourte(d) {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const o = { day: '2-digit', month: '2-digit' };
  if (x.getFullYear() !== new Date().getFullYear()) o.year = '2-digit';
  return x.toLocaleDateString('fr-FR', o);
}
// Date sans heure (« 2026-09-29 ») découpée telle quelle (pas de décalage UTC).
const jour = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || ''); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
// « aujourd’hui à 11:19 », sinon « le 21/10 à 11:19 ».
function leQuand(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const h = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return d.toDateString() === new Date().toDateString() ? `aujourd’hui à ${h}` : `le ${dateCourte(iso)} à ${h}`;
}
const ilYA = (jours) => {
  const d = new Date(); d.setDate(d.getDate() - jours);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nf = (v, dec = 0) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec }));

function NoteGrande({ nps, statut, taille }) {
  if (statut === 'echec') return <span className={`mb-sat-note mb-sat-note--det${taille ? ` mb-sat-note--${taille}` : ''}`} aria-label="Envoi jamais parti">!</span>;
  return (
    <span className={`mb-sat-note mb-sat-note--${tonNote(nps)}${taille ? ` mb-sat-note--${taille}` : ''}`}
      aria-label={aNote({ nps }) ? `Note ${nps} sur 10` : 'Pas de note'}>
      {aNote({ nps }) ? Number(nps) : '—'}
    </span>
  );
}

// Libellés exacts de la colonne « Suivi » de l'ordinateur.
function Suivi({ e }) {
  if (e.statut === 'echec') return <Pill ton="ko">Jamais parti</Pill>;
  if (e.statut !== 'repondu') return <Pill>En attente</Pill>;
  if (e.traite_le) {
    const par = [TRAITEMENT_LIBELLE[e.canal_traitement],
      e.traite_par_prenom ? `${e.traite_par_prenom} ${(e.traite_par_nom || '').charAt(0)}.`.trim() : null,
      dateCourte(e.traite_le)].filter(Boolean).join(' · ');
    return <Pill ton="ok">✓ Traité{par ? ` · ${par}` : ''}</Pill>;
  }
  return estDetracteur(e) ? <Pill ton="ko">À rappeler</Pill> : <Pill ton="attente">À traiter</Pill>;
}

// Ordre de la file : à rappeler, puis à traiter, puis traitées ; dans chaque
// groupe, la réponse la plus récente d'abord.
const rang = (e) => (e.traite_le ? 2 : estDetracteur(e) ? 0 : 1);
const groupe = (e) => ['À rappeler', 'À traiter', 'Traitées'][rang(e)];

export default function SatisfactionMobile({ concessions, onVersionOrdinateur }) {
  const [envois, setEnvois] = useState([]);
  const [files, setFiles] = useState(null);
  const [chiffres, setChiffres] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [file, setFile] = useState('a_traiter');
  const [recherche, setRecherche] = useState('');
  const [peutModifier, setPeutModifier] = useState(false);
  const [version, setVersion] = useState(0); // recharge la fiche ouverte
  const [toast, afficher] = useToast();

  const charger = useCallback(async () => {
    try {
      const params = { statut: 'repondu' };
      if (file !== 'toutes') params.traitement = file;
      if (recherche.trim()) params.search = recherche.trim();
      const { data } = await api.getEnvois(params);
      setEnvois(data.envois || []);
      setFiles(data.files || null);
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les réponses n’ont pas pu être chargées.'));
    } finally {
      setChargement(false);
    }
  }, [file, recherche]);

  // Mêmes chiffres que le tableau de bord de l'ordinateur : score NPS
  // (promoteurs − détracteurs) et note moyenne sur 30 jours ; les détracteurs
  // à rappeler, eux, sans limite de date (un oubli de septembre reste dû).
  const chargerChiffres = useCallback(async () => {
    try {
      const [mois, tout] = await Promise.all([api.tableauBord({ du: ilYA(30) }), api.tableauBord({})]);
      setChiffres({ mois: mois.data.total || {}, tout: tout.data.total || {} });
    } catch (e) {
      setChiffres(null);
    }
  }, []);

  useEffect(() => { setChargement(true); charger(); }, [charger]);
  useEffect(() => {
    chargerChiffres();
    permissionsApi.checkAccess('retours_prospects')
      .then((r) => setPeutModifier(!!r.data?.permissions?.peut_modifier))
      .catch(() => setPeutModifier(false));
  }, [chargerChiffres]);

  const visibles = useMemo(() => [...envois].sort((a, b) => rang(a) - rang(b)
    || new Date(b.repondu_le || b.envoye_le) - new Date(a.repondu_le || a.envoye_le)), [envois]);
  const cle = useCallback((e) => e.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  const traite = ({ suivi, canal }) => {
    afficher(suivi ? 'Suivi ajouté' : canal === 'sms' ? 'SMS envoyé · réponse traitée'
      : canal === 'mail' ? 'E-mail envoyé · réponse traitée' : 'Réponse marquée traitée');
    // Dans une file « à traiter », la réponse en sort : on passe à la suivante.
    if (!suivi && file !== 'traite' && file !== 'toutes') {
      if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    }
    setVersion((v) => v + 1);
    charger();
    chargerChiffres();
  };

  // Concession du sélecteur global : elle filtre toute la page sans que rien
  // ne le dise, on la rappelle donc en tête.
  const idFiltre = (() => { try { return localStorage.getItem('crm-global-concession'); } catch (e) { return null; } })();
  const nomFiltre = idFiltre ? (concessions || []).find((c) => String(c.id) === String(idFiltre))?.nom : null;

  const m = chiffres?.mois || {};
  const t = chiffres?.tout || {};

  return (
    <div className="mb-page">
      {nomFiltre && (
        <p className="mb-aide mb-sat-perimetre">Concession <b>{nomFiltre}</b> · choisie dans le menu utilisateur</p>
      )}
      <Chiffres items={[
        { libelle: 'NPS · 30 jours', valeur: m.score_nps === null || m.score_nps === undefined ? '—' : `${m.score_nps > 0 ? '+' : ''}${m.score_nps}` },
        m.note_moyenne !== null && m.note_moyenne !== undefined
          ? { libelle: 'note moyenne', valeur: `${nf(m.note_moyenne, 1)} /5` }
          : { libelle: 'note moyenne', valeur: m.nps_moyen === null || m.nps_moyen === undefined ? '—' : `${nf(m.nps_moyen, 1)} /10` },
        { libelle: 'détracteurs à rappeler', valeur: nf(t.detracteurs_a_traiter), alerte: (t.detracteurs_a_traiter || 0) > 0 },
      ]} />
      <div className="mb-outils">
        <Recherche id="mb-sat-q" valeur={recherche} onChange={setRecherche} delai={400} placeholder="Nom, téléphone, e-mail ou plaque" />
      </div>
      <Puces label="File" valeur={file} onChange={setFile}
        options={FILES.map((f) => ({ ...f, compte: files ? (files[f.cle] ?? 0) : null }))} />

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={files?.toutes === 0 && !recherche.trim()
          ? 'Aucune réponse reçue pour le moment. Les envois en attente de retour sont sur la version ordinateur (onglet Envois).'
          : file === 'a_traiter' || file === 'detracteurs_a_traiter' ? 'Rien à traiter.' : 'Aucune réponse.'}
        groupe={groupe}
        onOuvrir={fiche.ouvrir}
        carte={(e) => ({
          couleur: e.traite_le ? 'transparent' : estDetracteur(e) ? 'var(--color-error)' : 'var(--color-warning)',
          contenu: (
            <>
              <CarteLigne
                avant={<NoteGrande nps={e.nps} statut={e.statut} />}
                titre={<>{nomClient(e)} <VehiculeEnvoi immat={e.immat} chassis={e.chassis} statique /></>}
                sous={[etiquetteFormulaire(e.formulaire_source, e.formulaire_nom), e.site_nom || e.site_code].filter(Boolean).join(' · ')}
                age={depuis(e.repondu_le)}
                retard={!e.traite_le && estDetracteur(e)}
              />
              <span className={`mb-carte-texte${e.verbatim ? '' : ' mb-sat-muet'}`}>{e.verbatim || 'Pas de commentaire'}</span>
              <span className="mb-carte-meta"><Suivi e={e} /></span>
            </>
          ),
        })}
      />
      <LienOrdinateur onClick={onVersionOrdinateur}>Envois, tableau de bord, formulaires : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheReponse
          key={fiche.ouverte.id}
          ligne={fiche.ouverte}
          version={version}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          peutModifier={peutModifier}
          onTraite={traite}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheReponse({ ligne, version, position, total, onRetour, onPrecedent, onSuivant, peutModifier, onTraite, onVersionOrdinateur }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [feuille, setFeuille] = useState(null); // { canal, suivi }
  const appelEnCours = useRef(false);

  const charger = useCallback(() => {
    api.getEnvoi(ligne.id)
      .then((r) => { setData(r.data); setErreur(''); })
      .catch((e) => setErreur(messageErreur(e, 'La réponse n’a pas pu être chargée.')));
  }, [ligne.id]);
  useEffect(() => { charger(); }, [charger, version]);

  const e = data?.envoi || ligne;
  const repondu = e.statut === 'repondu';
  const estTraite = !!e.traite_le;
  const peutTraiter = peutModifier && repondu;
  const ouvrir = (canal) => setFeuille({ canal, suivi: estTraite });

  // Appeler → revenir : au retour dans l'appli après un appel lancé d'ici, la
  // feuille « Traiter » s'ouvre sur le canal Appel, prête pour la note.
  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState !== 'visible' || !appelEnCours.current) return;
      appelEnCours.current = false;
      if (peutTraiter && !estTraite) setFeuille((f) => f || { canal: 'telephone', suivi: false });
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [peutTraiter, estTraite]);
  const surClic = (ev) => { if (ev.target.closest && ev.target.closest('a[href^="tel:"]')) appelEnCours.current = true; };

  const reponses = data?.reponses || [];
  const npsLigne = reponses.find((r) => r.type === 'nps' && r.valeur_num !== null && r.valeur_num !== undefined);
  const nps = npsLigne ? Number(npsLigne.valeur_num) : (aNote(ligne) ? Number(ligne.nps) : null);
  const detracteur = nps !== null && nps <= 6;
  const textes = reponses.filter((r) => r.type === 'texte' && r.valeur_texte);
  const repondues = reponses.filter((r) => r.type !== 'texte' && aRepondu(r));
  const sansReponse = reponses.filter((r) => !aRepondu(r));
  const traitements = data?.traitements || [];
  const dernier = traitements.length ? traitements[traitements.length - 1] : null;
  const etiquette = etiquetteFormulaire(e.formulaire_source, e.formulaire_nom);
  const passage = estPassage(e.formulaire_source);

  const etat = e.statut === 'echec' ? <Pill ton="ko">Envoi jamais parti</Pill>
    : !repondu ? <Pill>En attente de réponse</Pill>
      : estTraite ? <Pill ton="ok">✓ Traité</Pill>
        : detracteur ? <Pill ton="ko">Détracteur · à rappeler</Pill>
          : <Pill ton="attente">À traiter</Pill>;

  return (
    <Fiche
      titre={`Réponse ${etiquette}`}
      label={`Réponse de ${nomClient(e)}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={peutTraiter && (estTraite
        ? <button type="button" className="mb-bouton" onClick={() => ouvrir('telephone')}>+ Ajouter un suivi</button>
        : <button type="button" className="mb-bouton mb-bouton--principal" onClick={() => ouvrir('telephone')}>Traiter</button>)}
    >
      <div className="mb-entete" onClickCapture={surClic}>
        <div className="mb-sat-tete">
          <NoteGrande nps={nps} statut={e.statut} taille="fiche" />
          <div className="mb-sat-tete-texte">
            <div className="mb-entete-titre">{nomClient(e)}</div>
            <VehiculeEnvoi immat={e.immat} chassis={e.chassis} />
            {etat}
          </div>
        </div>
        <div className="mb-entete-sous">
          {[etiquette, e.site_nom || e.site_code, e.marque,
            jour(e.date_vente) && `${passage ? 'passage le' : 'vendu le'} ${jour(e.date_vente)}`].filter(Boolean).join(' · ')}
        </div>
        <ActionsContact
          tel={e.client_telephone}
          email={e.client_email || ''}
          onSms={peutTraiter ? () => ouvrir('sms') : undefined}
          onEmail={peutTraiter ? () => ouvrir('mail') : undefined}
        />
      </div>

      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}

      {textes.length > 0 ? textes.map((r) => (
        <div key={r.question_id} className="mb-citation">
          {r.valeur_texte}
          <small>{textes.length > 1 ? r.libelle : 'Ce que le client a écrit'}</small>
        </div>
      )) : repondu && data && <p className="mb-aide mb-aide--centre">Le client n’a pas laissé de commentaire.</p>}

      {repondu && estTraite && (
        <div className="mb-carton mb-sat-traite">
          <b>✓ Traité par {CANAL_LABEL[e.canal_traitement] || '—'} {leQuand(e.traite_le)}
            {dernier && (dernier.user_prenom || dernier.user_nom) && ` · ${[dernier.user_prenom, dernier.user_nom].filter(Boolean).join(' ')}`}</b>
          {dernier?.note_operateur && <span>{dernier.note_operateur}</span>}
          {dernier?.message_envoye && (
            <div className="mb-citation">{dernier.objet && <b>{dernier.objet}{'\n'}</b>}{dernier.message_envoye}</div>
          )}
        </div>
      )}
      {!repondu && (
        <p className="mb-aide mb-aide--centre">
          {e.statut === 'echec'
            ? 'Le questionnaire n’est jamais parti : ce client n’a rien reçu, il n’y a rien à traiter.'
            : 'Le client n’a pas encore répondu. Il n’y a rien à traiter pour l’instant.'}
        </p>
      )}

      <Bloc titre="Ses réponses" compte={data ? repondues.length : null} ouvert>
        {!data && <p className="mb-aide">Chargement…</p>}
        {repondues.map((r) => (
          <div key={r.question_id} className="mb-sat-question">
            <span className="mb-sat-libelle">{r.libelle}</span>
            <Valeur r={r} consentements={data.consentements} />
          </div>
        ))}
        {data && !repondues.length && <p className="mb-aide">Aucune réponse en dehors du commentaire.</p>}
        {repondu && sansReponse.length > 0 && (
          <p className="mb-aide">{sansReponse.length} question{sansReponse.length > 1 ? 's' : ''} sans réponse.</p>
        )}
      </Bloc>

      <Bloc titre="Parcours" resume={e.envoye_le ? `envoyé ${dateCourte(e.envoye_le)}` : ''}>
        <ol className="mb-sat-frise">
          {jour(e.date_vente) && <li><span>{passage ? 'Passage atelier' : 'Vente'}</span><time>{jour(e.date_vente)}</time></li>}
          <li className={e.statut === 'echec' ? 'is-alerte' : ''}>
            <span>
              {e.statut === 'echec' ? 'Envoi échoué' : 'Questionnaire envoyé'} · {CANAL_ENVOI[e.canal_envoi] || e.canal_envoi || '—'}
              {e.note_canal && <small>{e.note_canal}</small>}
              {e.statut === 'echec' && e.erreur && <small>{e.erreur}</small>}
            </span>
            <time>{quand(e.envoye_le)}</time>
          </li>
          {repondu && (
            <li><span>Réponse reçue{e.canal_reponse ? ` par ${CANAL_ENVOI[e.canal_reponse] || e.canal_reponse}` : ''}</span><time>{quand(e.repondu_le)}</time></li>
          )}
          {traitements.map((tr) => (
            <li key={tr.id} className={tr.statut === 'echec' ? 'is-alerte' : ''}>
              <span>
                Traité par {CANAL_LABEL[tr.canal] || tr.canal}
                {(tr.user_prenom || tr.user_nom) && ` · ${[tr.user_prenom, tr.user_nom].filter(Boolean).join(' ')}`}
                {tr.brouillon_ia && ' · ✨ rédigé avec l’IA'}
                {tr.statut === 'echec' && <small>Échec : {tr.erreur}</small>}
                {tr.note_operateur && <small>{tr.note_operateur}</small>}
                {tr.message_envoye && <small>Message : {tr.message_envoye}</small>}
              </span>
              <time>{quand(tr.created_at)}</time>
            </li>
          ))}
          {repondu && !estTraite && (
            <li className="is-maintenant"><span>{detracteur ? 'À rappeler' : 'À traiter'}</span><time>maintenant</time></li>
          )}
        </ol>
      </Bloc>

      <Bloc titre="Coordonnées">
        <Kv lignes={[['Tél.', e.client_telephone || 'aucun'], ['E-mail', e.client_email || 'aucun']]} />
      </Bloc>

      {!peutModifier && repondu && (
        <p className="mb-aide mb-aide--centre">Lecture seule : traiter une réponse demande le droit « modifier » sur cette page.</p>
      )}
      <p className="mb-aide mb-aide--centre">Envois, tableau de bord, annulation du marquage : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {feuille && (
        <FeuilleTraiter
          envoi={e}
          canalInitial={feuille.canal}
          suivi={feuille.suivi}
          onFermer={() => setFeuille(null)}
          onEchec={charger}
          onFait={(canal) => { setFeuille(null); onTraite({ suivi: feuille.suivi, canal }); }}
        />
      )}
    </Fiche>
  );
}

const aRepondu = (r) => (r.valeur_bool !== null && r.valeur_bool !== undefined)
  || (r.valeur_num !== null && r.valeur_num !== undefined)
  || Boolean(r.valeur_texte)
  || (r.type === 'recontact' && Object.keys(r.valeur_json?.choix || {}).length > 0);

function Valeur({ r, consentements = [] }) {
  if (r.type === 'recontact') {
    const choix = r.valeur_json?.choix || {};
    const sorts = consentements.filter((c) => c.choix && Object.keys(c.choix).length);
    return (
      <span className="mb-sat-valeur">
        {Object.keys(choix).map((k) => `${FINALITE[k] || k} ${choix[k] ? '✓' : '—'}`).join(' · ')}
        {sorts.map((c) => (
          <small key={c.id}>
            {c.statut === 'enregistre' ? '✓ enregistré dans A97 RGPD'
              : c.statut === 'a_envoyer' ? '⏳ en attente de transmission à A97 RGPD'
                : c.statut === 'echec' ? `✕ refusé par A97 RGPD${c.erreur ? ` : ${c.erreur}` : ''}` : c.statut}
          </small>
        ))}
      </span>
    );
  }
  if (r.valeur_bool !== null && r.valeur_bool !== undefined) {
    return <span className="mb-sat-valeur"><Pill ton={r.valeur_bool ? 'ok' : 'ko'}>{r.valeur_bool ? 'Oui' : 'Non'}</Pill></span>;
  }
  if (r.valeur_num !== null && r.valeur_num !== undefined) {
    const n = Number(r.valeur_num);
    if (r.type === 'nps') {
      return <span className="mb-sat-valeur"><span className={`mb-sat-nps mb-sat-note--${tonNote(n)}`}>{n}</span> / 10</span>;
    }
    return (
      <span className="mb-sat-valeur mb-sat-etoiles" aria-label={`${n} sur 5`}>
        {'★'.repeat(Math.max(0, Math.min(5, n)))}<span className="mb-sat-etoiles-vides">{'★'.repeat(Math.max(0, 5 - n))}</span>
        <small>{n}/5</small>
      </span>
    );
  }
  return <span className="mb-sat-valeur">{r.valeur_texte}</span>;
}

/**
 * Traitement d'une réponse, mêmes champs que SatisfactionReponseModal.js :
 * Appel / Sur place → ce qui a été dit (obligatoire) ; SMS / E-mail → une
 * phrase d'intention, ✨ Rédiger (IA), relecture, puis envoi. Un brouillon IA
 * ne fait pas perdre un message déjà écrit : « ↶ Restaurer mon texte ».
 */
function FeuilleTraiter({ envoi, canalInitial, suivi, onFermer, onEchec, onFait }) {
  const [canal, setCanal] = useState(canalInitial || 'telephone');
  const [note, setNote] = useState('');
  const [objet, setObjet] = useState(`${envoi.site_nom || envoi.concession_nom || 'Votre concession'} : suite à votre retour`);
  const [message, setMessage] = useState('');
  const [brouillonIa, setBrouillonIa] = useState(false);
  const [precedent, setPrecedent] = useState(null);
  const [redaction, setRedaction] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const emet = CANAUX.find((c) => c.cle === canal)?.emet;
  const destinataire = canal === 'sms' ? envoi.client_telephone : canal === 'mail' ? envoi.client_email : null;
  const longueur = longueurSms(message);
  const pret = !envoiEnCours && !redaction && (emet
    ? !!message.trim() && !!destinataire && (canal !== 'sms' || longueur <= SMS_MAX) && (canal !== 'mail' || !!objet.trim())
    : !!note.trim());
  const libelle = envoiEnCours ? 'Envoi…'
    : canal === 'sms' ? 'Envoyer le SMS et marquer traité'
      : canal === 'mail' ? 'Envoyer l’e-mail et marquer traité' : 'Marquer traité';

  const choisirCanal = (c) => { setCanal(c); setBrouillonIa(false); setPrecedent(null); setErreur(''); };

  const rediger = async () => {
    if (!note.trim() || redaction) return;
    setRedaction(true); setErreur('');
    try {
      const { data } = await api.brouillon(envoi.id, { canal, note_operateur: note });
      if (!data.disponible) {
        setErreur('La rédaction assistée est indisponible pour le moment : écrivez le message vous-même.');
      } else {
        if (message.trim() && message !== data.brouillon) setPrecedent(message);
        setMessage(data.brouillon);
        setBrouillonIa(true);
      }
    } catch (e) {
      setErreur(messageErreur(e, 'Rédaction impossible'));
    } finally {
      setRedaction(false);
    }
  };

  const valider = async () => {
    if (!pret) return;
    setEnvoiEnCours(true); setErreur('');
    try {
      const { data } = await api.traiter(envoi.id, {
        canal, note_operateur: note, objet: canal === 'mail' ? objet : null,
        message: emet ? message : null, brouillon_ia: brouillonIa,
      });
      if (data.statut === 'echec') {
        // Un SMS ou un e-mail en échec laisse la réponse dans la file.
        setErreur(`L’envoi a échoué${data.traitement?.erreur ? ` : ${data.traitement.erreur}` : ''}. La réponse reste à traiter.`);
        setEnvoiEnCours(false);
        onEchec();
        return;
      }
      onFait(canal);
    } catch (e) {
      setErreur(messageErreur(e, 'Enregistrement impossible'));
      setEnvoiEnCours(false);
    }
  };

  return (
    <Feuille titre={suivi ? 'Ajouter un suivi' : 'Traiter la réponse'} onFermer={onFermer} fermer="Annuler">
      <div className="mb-sat-feuille">
        <div className="mb-grille" style={{ '--mb-cols': 4 }} role="tablist" aria-label="Canal">
          {CANAUX.map((c) => (
            <button key={c.cle} type="button" role="tab" aria-selected={canal === c.cle}
              className={canal === c.cle ? 'is-actif' : ''} onClick={() => choisirCanal(c.cle)}>
              <span aria-hidden="true">{c.icone}</span>{c.libelle}
            </button>
          ))}
        </div>

        {!emet && (
          <div className="mb-champ">
            <label className="mb-etiquette" htmlFor="mb-sat-note">Ce qui a été dit au client (obligatoire)</label>
            <textarea id="mb-sat-note" className="mb-zone" value={note} onChange={(ev) => setNote(ev.target.value)}
              placeholder="Ex. : appelé ce matin, excuses pour l’attente, il était satisfait de l’explication." />
          </div>
        )}

        {emet && (
          <>
            <div className="mb-champ">
              <label className="mb-etiquette" htmlFor="mb-sat-intention">① Ce que vous voulez lui dire, en une phrase</label>
              <input id="mb-sat-intention" value={note} onChange={(ev) => setNote(ev.target.value)} enterKeyHint="go"
                onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); rediger(); } }}
                placeholder="Ex. : excuses pour l’attente, le chef d’atelier le rappelle lundi" />
              <div className="mb-rangee">
                <button type="button" className="mb-mini mb-mini--accent" onClick={rediger} disabled={redaction || !note.trim()}>
                  {redaction ? 'Rédaction…' : brouillonIa ? '↻ Autre version' : '✨ Rédiger'}
                </button>
                {precedent !== null && (
                  <button type="button" className="mb-mini" onClick={() => { setMessage(precedent); setPrecedent(null); setBrouillonIa(false); }}>
                    ↶ Restaurer mon texte
                  </button>
                )}
              </div>
            </div>
            <div className="mb-champ">
              <label className="mb-etiquette" htmlFor="mb-sat-message">② Relisez et corrigez</label>
              {brouillonIa && <p className="mb-aide">✨ Proposé par l’IA à partir de ses réponses.</p>}
              {canal === 'mail' && (
                <input id="mb-sat-objet" value={objet} onChange={(ev) => setObjet(ev.target.value)} aria-label="Objet de l’e-mail" placeholder="Objet" />
              )}
              <textarea id="mb-sat-message" className="mb-zone" value={message} onChange={(ev) => setMessage(ev.target.value)}
                placeholder="Touchez ✨ Rédiger, ou écrivez le message vous-même." />
              <div className="mb-rangee">
                <span className={`mb-aide${destinataire ? '' : ' mb-aide--erreur'}`}>
                  {destinataire ? `À ${destinataire}` : canal === 'sms' ? 'Aucun téléphone connu' : 'Aucun e-mail connu'}
                </span>
                {canal === 'sms' && <span className={`mb-compteur${longueur > SMS_MAX ? ' is-trop' : ''}`}>{longueur} / {SMS_MAX}</span>}
              </div>
            </div>
          </>
        )}

        {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
        <div className="mb-feuille-pied">
          <button type="button" className="mb-bouton" onClick={onFermer} disabled={envoiEnCours}>Annuler</button>
          <button type="button" className="mb-bouton mb-bouton--principal" onClick={valider} disabled={!pret}>{libelle}</button>
        </div>
      </div>
    </Feuille>
  );
}

